import { useData } from '@hooks/useData';
import { ID } from '@datassential/platform-ui-lib';
import { components } from '@apiSchema';
import http from '@core/fetch';
import apiConfig from '@apiConfig';
import { LrCategory } from './useHomeCommonData';

export type MenuLaunchesAndLtoActivityData = components['schemas']['MenuLaunchesAndLtoActivityViewModel'];

type CategoryDetail = components['schemas']['DetailsByCategoryViewModel'];

interface LtoAvgScoreRow {
    categoryId: ID;
    title: string;
    chainAverageScore: number | null;
    compareAverageScore: number | null;
}

interface MenuLaunchesLtoChartData {
    mainTotal: number;
    compareTotal: number;
    values: Array<{
        id: ID;
        title: string;
        mainValue: number;
        compareValue: number;
    }>;
}

interface LtoActivityResult {
    chartData: MenuLaunchesLtoChartData;
    averageScoreRows: LtoAvgScoreRow[];
}

const DEFAULT_DATA: LtoActivityResult = {
    chartData: { mainTotal: 0, compareTotal: 0, values: [] },
    averageScoreRows: [],
};

const REQUEST_TYPE: keyof typeof apiConfig = 'get menu launches and lto activity';

async function getMenuLaunchesAndLtoActivity(
    chainId: ID | null,
    comparePresetId: number,
    lrCategories: LrCategory[],
): Promise<LtoActivityResult> {
    if (chainId == null) return DEFAULT_DATA;

    try {
        const response: MenuLaunchesAndLtoActivityData = await http.get(REQUEST_TYPE, { chainId: Number(chainId) });

        const chainByCategory = new Map<ID, CategoryDetail>();
        const presetByCategory = new Map<ID, CategoryDetail>();
        const presetDetails = response.presetDetails?.find((p) => p.presetId === comparePresetId)?.details;

        for (const row of response.chainDetails?.detailsByCategories ?? []) {
            chainByCategory.set((row.categoryId ?? 0) as ID, row);
        }

        for (const row of presetDetails?.detailsByCategories ?? []) {
            presetByCategory.set((row.categoryId ?? 0) as ID, row);
        }

        const chartBars = lrCategories
            .map((category) => ({
                ...category,
                mainValue: chainByCategory.get(category.id)?.count ?? 0,
                compareValue: presetByCategory.get(category.id)?.count ?? 0,
            }))
            .sort((a, b) => b.mainValue - a.mainValue);

        const averageScoreRows = chartBars.map(({ id, title }) => ({
            categoryId: id,
            title,
            chainAverageScore: chainByCategory.get(id)?.averageScore ?? null,
            compareAverageScore: presetByCategory.get(id)?.averageScore ?? null,
        }));

        return {
            chartData: {
                mainTotal: response.chainDetails?.count ?? 0,
                compareTotal: presetDetails?.count ?? 0,
                values: chartBars,
            },
            averageScoreRows,
        };
    } catch (err) {
        console.error('Fetch error:', REQUEST_TYPE, err);
        return DEFAULT_DATA;
    }
}

export function useHomeMenuLaunchesAndLtoActivity(
    chainId: ID | null,
    comparePresetId: number,
    lrCategories: LrCategory[],
) {
    return useData(DEFAULT_DATA, getMenuLaunchesAndLtoActivity, chainId, comparePresetId, lrCategories);
}
